import { Component, computed, input } from '@angular/core';
import { UmssIconComponent } from '../icon/icon.component';

/**
 * Renders a contractor's Social Security Number as a mask plus its last four
 * digits (`*****7812`).
 *
 * Takes `ssnLast4` — never a full SSN. The API denies the complete number to
 * business and facility callers (see ContractorDtoRedactor), so masking here is
 * a display concern only; a component that accepted the whole value would put it
 * on the wire and in browser memory for audiences that must not receive it.
 *
 * Renders a neutral fallback when the caller is not entitled to even the last
 * four, so an absent value degrades quietly rather than showing a broken mask.
 */
@Component({
  selector: 'umss-ssn-masked',
  standalone: true,
  imports: [UmssIconComponent],
  templateUrl: './ssn-masked.component.html',
  styleUrl: './ssn-masked.component.scss'
})
export class UmssSsnMaskedComponent {
  /** Last four digits only. Null/absent when not visible to this caller. */
  ssnLast4 = input<string | null | undefined>(null);

  /** Shown when there is no value to display. */
  fallback = input('SSN not available');

  showIcon = input(true);

  iconSize = input('16px');

  protected readonly hasValue = computed(() => !!this.ssnLast4()?.trim());

  /**
   * Keeps the SSN's canonical XXX-XX-XXXX grouping so the field reads
   * unmistakably as an SSN rather than as an anonymous run of digits — it sits
   * beside a phone number and an email in the profile metadata.
   *
   * Bullets rather than asterisks: they match maskAccountNumber, the codebase's
   * other sensitive-identity mask, and they sit on the digits' baseline instead
   * of floating above it the way `*` does in most sans faces.
   */
  protected readonly masked = computed(
    () => `\u2022\u2022\u2022-\u2022\u2022-${this.ssnLast4()?.trim()}`
  );

  /**
   * The mask itself is announced character by character ("bullet bullet
   * bullet dash…"), so it is hidden from assistive tech and this is read out
   * instead.
   */
  protected readonly ariaLabel = computed(() =>
    this.hasValue()
      ? `Social Security Number ending in ${this.ssnLast4()?.trim()}`
      : this.fallback()
  );
}
